import { SectionBasedProduct } from "@brandclub/common-ui";
import {
  ConversationLoaderData,
  ProcessedConversationData,
} from "../types/conversation";
import { extractProductSkus } from "./productGroupParser";

/**
 * Process loaded conversation data into messages and product map
 */
export const processConversationData = (
  data: ConversationLoaderData | null,
  conversationId?: string
): ProcessedConversationData => {
  if (!data || !data.messages || data.messages.length === 0) {
    return {
      messages: [],
      productMap: new Map(),
      followUpQuestions: [],
      previewSearchQueries: [],
      previewQueryProducts: [],
    };
  }

  // Extract preview data from the last assistant message for backward compatibility
  // (used for conversation-level state management during streaming)
  const lastAssistantMessage = [...data.messages]
    .reverse()
    .find((m) => m.role === "assistant");
  const previewSearchQueries = lastAssistantMessage?.previewSearchQueries || [];
  const previewQueryProducts = lastAssistantMessage?.previewQueryProducts || [];

  // Create a map of products by stacklineSku
  const productMap = new Map<string, SectionBasedProduct>();
  if (data.products && Array.isArray(data.products)) {
    data.products.forEach((product: SectionBasedProduct) => {
      productMap.set(product.stacklineSku, product);
    });
  }

  // Convert loaded messages to conversation format
  let nextAssistantHasFeaturedProduct: {
    product: SectionBasedProduct;
    question: string;
  } | null = null;

  const messages = data.messages
    .filter((msg) => msg.role === "user" || msg.role === "assistant")
    .map((msg, index) => {
      const products: SectionBasedProduct[] = [];

      // Parse content if it's a JSON string (shouldn't happen if backend works correctly, but fallback)
      let messageContent = msg.content;
      if (
        typeof messageContent === "string" &&
        messageContent.startsWith("{")
      ) {
        try {
          const parsed = JSON.parse(messageContent);
          if (parsed.role === "user" && parsed.content) {
            messageContent = parsed.content;
          }
        } catch (e) {
          // Not JSON, use as-is
        }
      }

      let featuredProduct = undefined;

      // Check if this is a user message with featured product tag
      if (msg.role === "user") {
        const featuredProductRegex =
          /<product\s+featured="true"\s+stacklineSku="([^"]+)"(?:\s+question="([^"]+)")?\s*\/>/;
        const match = msg.content.match(featuredProductRegex);

        if (match) {
          const stacklineSku = match[1];
          const question = match[2] || "";
          const product = productMap.get(stacklineSku);

          // Display only the question in user message
          messageContent = question;

          // Store featured product for next assistant message
          if (product) {
            nextAssistantHasFeaturedProduct = { product, question };
          }
        }
      }

      // If it's an assistant message, extract and match products
      if (msg.role === "assistant") {
        // Extract ALL featured product tags from the assistant message (not just the first one)
        const featuredProductRegex =
          /<product\s+featured="true"\s+stacklineSku="([^"]+)"(?:\s+question="([^"]+)")?\s*\/>/g;
        const featuredMatches = [...msg.content.matchAll(featuredProductRegex)];

        if (featuredMatches.length > 0) {
          // Extract all featured products
          const extractedFeaturedProducts = featuredMatches
            .map((match) => {
              const stacklineSku = match[1];
              const question = match[2] || "";
              const product = productMap.get(stacklineSku);
              if (product) {
                return { product, question };
              }
              return null;
            })
            .filter(Boolean) as Array<{
            product: SectionBasedProduct;
            question: string;
          }>;

          if (extractedFeaturedProducts.length > 0) {
            // Store the first one in featuredProduct for backward compatibility
            // All featured products will be rendered inline via messageRenderer
            featuredProduct = extractedFeaturedProducts[0];
            // Clear any pending featured product from user message since this assistant message has its own
            nextAssistantHasFeaturedProduct = null;
          }
        } else {
          // No featured product in assistant message, check for regular products
          const productSkus = extractProductSkus(msg.content);
          productSkus.forEach((sku) => {
            const product = productMap.get(sku);
            if (product) {
              products.push(product);
            }
          });

          // Assign featured product from user message if available
          if (nextAssistantHasFeaturedProduct) {
            featuredProduct = nextAssistantHasFeaturedProduct;
            nextAssistantHasFeaturedProduct = null; // Clear after assigning
          }
        }

        // Attach preview data to assistant messages if present
        // This allows each assistant message to display its own chain of thought
      }

      // For user messages, handle images from server (S3 URLs)
      let imageUrl: string | undefined;
      let imageMimeType: string | undefined;

      if (msg.role === "user" && msg.imageUrl) {
        imageUrl = msg.imageUrl;
        imageMimeType = msg.imageMimeType;
      }

      return {
        role: msg.role as "user" | "assistant",
        content: messageContent,
        timestamp: new Date(msg.timestamp),
        messageIndex: msg.messageIndex, // Include messageIndex for retry/feedback operations
        feedback: msg.feedback, // Include feedback state
        products,
        ...(featuredProduct && { featuredProduct }),
        // Attach preview data to individual assistant messages
        ...(msg.role === "assistant" &&
          msg.previewSearchQueries && {
            previewSearchQueries: msg.previewSearchQueries,
          }),
        ...(msg.role === "assistant" &&
          msg.previewQueryProducts && {
            previewQueryProducts: msg.previewQueryProducts,
          }),
        // Attach store locator to assistant messages
        ...(msg.role === "assistant" &&
          msg.storeLocator && { storeLocator: msg.storeLocator }),
        // Attach follow-up queries to assistant messages
        ...(msg.role === "assistant" &&
          msg.followUpQueries && { followUpQueries: msg.followUpQueries }),
        // Attach image URL for user messages (from S3)
        ...(imageUrl && { imageUrl, imageMimeType }),
      };
    });

  return {
    messages,
    productMap,
    followUpQuestions: data.followUpQuestions || [],
    previewSearchQueries,
    previewQueryProducts,
  };
};
